import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { AnswerCountCard } from '@/components/molecules/survey/answer-count-card'
import { AnswerRateCard } from '@/components/molecules/survey/answer-rate-card'
import { NpsGaugeCard } from '@/components/molecules/survey/nps-gauge'
import { TimelineChart } from '@/components/molecules/survey/timeline-chart'
import { NpsSummaryResponse, MonthlyTrend } from '@/models/survey/nps-summary'
import { Dayjs } from 'dayjs'

/**
 * 集計ページのメインセクションコンポーネント
 * 回答数、回答率、NPSゲージ、タイムラインチャートを表示
 */
type Props = {
  npsSummary: NpsSummaryResponse | null
  month: Dayjs
}

export const SurveySummaryMainSection: React.FC<Props> = ({ npsSummary, month }) => {
  const { t } = useTranslation()

  const summary = npsSummary?.summary
  const prevComparison = summary?.prevMonthComparison
  // 前月のデータを取得（前月比計算用）
  const prevMonthData = npsSummary?.monthlyTrends?.find(trend => trend.month === month.subtract(1, 'month').format('YYYY-MM'))

  // デフォルトのNPS breakdown（データがない場合）
  const defaultNpsBreakdown = {
    promoters: 0,
    passives: 0,
    detractors: 0,
    promotersPct: 0,
    passivesPct: 0,
    detractorsPct: 0,
  }

  // デフォルトのmonthlyTrends（データがない場合）
  const defaultMonthlyTrends: MonthlyTrend[] = []

  return (
    <div css={twoColumnLayoutStyle}>
      {/* Left Column: Split into left (Answer Count, Answer Rate) and right (NPS Gauge) */}
      <div css={leftColumnStyle}>
        {/* Left half: Answer Count and Answer Rate stacked vertically */}
        <div css={leftHalfStyle}>
          <AnswerCountCard
            title={t('AnswerCount')}
            currentMonthValue={summary?.totalAnswers ?? 0}
            momRate={prevComparison?.answerCountChange}
            prevMonthValue={prevMonthData?.totalAnswers}
          />
          <AnswerRateCard
            title={t('AnswerRate')}
            currentMonthValue={summary?.answerRate ?? 0}
            momRate={prevComparison?.answerRateChange}
            prevMonthAnswerRate={prevMonthData?.answerRate}
          />
        </div>
        {/* Right half: NPS Gauge */}
        <div css={rightHalfStyle}>
          <div css={npsGaugeWrapperStyle}>
            <NpsGaugeCard
              npsScore={summary?.npsScore ?? 0}
              breakdown={summary?.npsBreakdown ?? defaultNpsBreakdown}
              prevNpsChange={prevComparison?.npsChange}
              prevNpsScore={prevMonthData?.npsScore}
            />
          </div>
        </div>
      </div>

      {/* Right Column: Timeline Chart */}
      <div css={rightColumnStyle}>
        <TimelineChart data={npsSummary?.monthlyTrends ?? defaultMonthlyTrends} />
      </div>
    </div>
  )
}

const twoColumnLayoutStyle = css({
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  padding: '24px 32px',
  backgroundColor: '#FFF',
  alignItems: 'stretch',
  width: '100%',
  boxSizing: 'border-box',
  minWidth: '1000px',
})

const leftColumnStyle = css({
  display: 'flex',
  flexDirection: 'row',
  flex: '0 0 40%',
  alignItems: 'stretch',
})

const leftHalfStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
  flex: '1 1 50%',
  height: '100%',
  '> div:first-child': {
    borderRight: 'none',
  },
  '> div:last-child': {
    borderTop: 'none',
    borderRight: 'none',
  },
})

const rightHalfStyle = css({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 50%',
  height: '100%',
})

const rightColumnStyle = css({
  flex: '1 1 60%',
  overflow: 'hidden',
})

const npsGaugeWrapperStyle = css({
  padding: '10px',
  background: '#fff',
  borderRadius: '4px',
  border: '1px solid #D9D9D9',
  height: '100%',
  display: 'flex',
})
